import { Cell, Row, Workbook, Worksheet } from 'exceljs';
import { saveAs } from 'file-saver';
import { get, set } from 'lodash';
import { getCurrentDayString, toMoneyDisplay } from '@/helpers';
import { RewardPeriodExportData } from '@/models';
import { STYLES, NA, Style, CallbackFn } from './project-exporting-config';

export default class RewardPeriodsExport {
  private workbook = new Workbook();
  private ws: Worksheet;
  private exportData: RewardPeriodExportData[];
  private columnWidths = [30, 30, 15, 15, 15, 15, 15, 15, 15, 30, 30];

  constructor(exportData: RewardPeriodExportData[]) {
    const sheetName = 'Reward Periods';
    this.exportData = exportData;
    this.ws = this.workbook.addWorksheet(sheetName);
  }

  addStyleToCell(cell: Cell, style: Style = {}): void {
    Object.keys(style).map(key => set(cell, key, get(style, key)));
  }

  get exportFileName(): string {
    return `HIVE_Reward_Periods_Result_${getCurrentDayString()}.xlsx`;
  }

  addRow(data: unknown[], style?: Style, callback?: CallbackFn): Row {
    const row = this.ws.addRow(data);
    row.eachCell((cell, index) => {
      if (typeof cell.value === 'number') {
        cell.alignment = STYLES.middleRightWrappedAlignment;
      } else {
        cell.alignment = STYLES.middleLeftWrappedAlignment;
      }
      if (cell.value === NA) {
        cell.font = STYLES.naFont;
        cell.alignment = STYLES.middleRightWrappedAlignment;
      }
      this.addStyleToCell(cell, style);
      if (callback) {
        callback(cell, index);
      }
    });
    return row;
  }

  interleaveRowStyle(data: unknown[], index: number, callbackFn?: CallbackFn): Row {
    return index % 2 === 0
      ? this.addRow(data, { fill: STYLES.lightTealFill }, callbackFn)
      : this.addRow(data, {}, callbackFn);
  }

  addTableHeaderRow(header: { title: string; style?: Style }[]): void {
    const titles = header.map(({ title }) => title);
    const row = this.ws.addRow(titles);
    row.eachCell((cell, idx) => {
      const { font = STYLES.whiteBoldFont, fill = STYLES.primaryTealFill } = header[idx - 1]?.style || {};
      cell.font = font;
      cell.fill = fill;
    });
  }

  buildRowInformation(data: RewardPeriodExportData): unknown[] {
    const { user, item, cost, estimatedPrice, actualPrice, createdAt, resolvedAt, status, reason, category, period } =
      data || {};
    return [
      user,
      item,
      category,
      cost,
      toMoneyDisplay(estimatedPrice),
      toMoneyDisplay(actualPrice),
      createdAt,
      resolvedAt,
      status,
      reason || NA,
      period
    ];
  }

  addInformationToWorksheet(): void {
    const header = [
      { title: 'User' },
      { title: 'Item' },
      { title: 'Category', style: { fill: STYLES.lightOrangeFill } },
      { title: 'Cost' },
      { title: 'Estimated Price' },
      { title: 'Actual Price' },
      { title: 'Created At' },
      { title: 'Resolved At' },
      { title: 'Status' },
      { title: 'Reason' },
      { title: 'Period', style: { fill: STYLES.lightOrangeFill } }
    ];
    this.addTableHeaderRow(header);
    const rows = this.exportData
      .sort((a, b) => {
        const firstDate = new Date(a.createdAt || '');
        const secondDate = new Date(b.createdAt || '');
        return firstDate.getTime() - secondDate.getTime();
      })
      .map(d => this.buildRowInformation(d));

    rows.forEach((rowData, rowIdx) => {
      this.interleaveRowStyle(rowData, rowIdx);
    });
  }

  setColumnWidth(): void {
    this.ws.columns.forEach((column, index) => {
      column.width = this.columnWidths[index];
    });
  }

  addBorder(): void {
    this.ws.eachRow({ includeEmpty: true }, row => {
      row.eachCell({ includeEmpty: true }, cell => {
        const style = STYLES.thinGreyBorder;
        cell.border = {
          top: cell?.border?.top || style,
          bottom: cell?.border?.bottom || style,
          left: cell?.border?.left || style,
          right: cell?.border?.right || style
        };
      });
    });
  }

  async generateXLSXFile(): Promise<void> {
    this.addInformationToWorksheet();
    this.setColumnWidth();
    this.addBorder();

    try {
      const buf = await this.workbook.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, this.exportFileName);
    } catch (error) {
      console.log(error);
    }
  }
}
